import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Event } from 'pkg/api/models/event';
import type { EventSeries } from 'pkg/api/models/event_series';
import type { Group } from 'pkg/api/models/group';
import type { LibraryTag } from 'pkg/api/models/library_tag';
import type { Record } from 'pkg/api/models/record';
import type { Schedule } from 'pkg/api/models/schedule';

export enum PracticePlanStatus {
	Draft = 0,
	Active = 1,
	Archived = 2,
}

export enum PracticePlanGroupStatus {
	Configuring = 0,
	Generated = 1,
	Released = 2,
}

export enum PracticePlanGroupEventSource {
	// Created by the plan's schedule publish.
	Allocation = 0,
	// A pre-existing calendar event pulled into the plan by date range.
	Adopted = 1,
}

export interface PracticePlanGroupEvent extends Record, Dateable {
	practicePlanGroupId: number;
	eventId: number;
	source: PracticePlanGroupEventSource;
}

export interface PracticePlanGroup extends Record, Dateable, AllowedActionable {
	practicePlanId: number;
	groupId: number;
	// The practice-structure template: a training session this plan group
	// owns, whose blocks are the segments every generated practice follows.
	// Absent only on plan groups from before the structure existed — see
	// ensureTemplateSession.
	templateSessionId?: number;
	generateSessions: boolean;
	useCuratedContent: boolean;
	// The team's own library in the generation pool. At least one of the two
	// content sources stays on.
	useOwnContent: boolean;
	// Whether generated sessions open with a 10-minute warm-up block ahead of
	// the exercise blocks. It is filled only from exercises tagged as a
	// warm-up and stays empty when the library has none.
	includeWarmup: boolean;
	// How many exercise blocks a generated session has; the structure
	// template is rebuilt as this many blocks, the warm-up not counted.
	blockCount: number;
	// How many exercises the planner puts in each block.
	exercisesPerBlock: number;
	status: PracticePlanGroupStatus;

	// The club the tab belongs to. Loaded by the plan endpoints so the tab can
	// be labelled without a lookup per row.
	group?: Group;
	events?: PracticePlanGroupEvent[];
	// The whole-team focus mix, heaviest weight first. Loaded by the plan and
	// plan-group endpoints; the engine falls back to it for every block and
	// session group without a focus of its own.
	focus?: PracticePlanGroupFocus[];
	// How long the plan group's practices run for (the most common length in
	// the practice set), which the structure template's segments must add up
	// to. Absent while the practice set is empty, and only filled in by the
	// plan-group show endpoint.
	slotDurationMinutes?: number;
}

export interface PracticePlanGroupFocus extends Record, Dateable {
	practicePlanGroupId: number;
	libraryTagId: number;
	weight: number;
	libraryTag?: LibraryTag;
}

export interface PracticePlan extends Record, Dateable, AllowedActionable {
	organizationId: number;
	groupId: number;
	// Set when the plan owns a schedule (the org-led allocation); absent when
	// the plan only targets already-existing practice events.
	scheduleId?: number;
	title: string;
	// Unix timestamps bounding the training period.
	startsAt: number;
	endsAt: number;
	status: PracticePlanStatus;
	createdByUserId?: number;

	groups?: PracticePlanGroup[];
	// The linked allocation schedule, loaded by the plan show endpoint so the
	// workspace can present it to coaches who cannot read schedules
	// themselves. Its published periods say whether the allocation has
	// reached the calendar.
	schedule?: Schedule;
	// How deep into the plan its teams are. Only on the plans list.
	progress?: PracticePlanProgress;
}

export interface PracticePlanProgress {
	practices: number;
	completed: number;
}

export interface PracticePlanCreatePayload {
	title: string;
	startsAt: number;
	endsAt: number;
	// The org unit that will own the plan; omit to own it at the organization
	// root.
	groupId?: number;
	groupIds?: number[];
	// Links the plan to an org-owned schedule from the start. Needs the
	// scheduling module and write, on top of creating plans.
	scheduleId?: number;
}

export interface PracticePlanUpdatePayload {
	title?: string;
	startsAt?: number;
	endsAt?: number;
	status?: PracticePlanStatus;
	// Attaching (scheduleId) or detaching (clearSchedule) the allocation
	// schedule takes Action.PracticePlanManageSchedule, unlike the other
	// fields. Linking an already-published schedule pulls its practices into
	// every team's practice set straight away.
	scheduleId?: number;
	clearSchedule?: boolean;
}

export interface PracticePlanDuplicatePayload {
	title: string;
}

export interface PracticeCandidate {
	event: Event;
	inPracticeSet: boolean;
	// Provenance of the membership; null when the practice is not in the set.
	source: PracticePlanGroupEventSource | null;
	// The event's own length, which is what the practice structure template
	// gets measured against.
	durationMinutes: number;
	// The practice does not run as long as the plan's others, so the template
	// will not fit it as-is.
	durationDiffers: boolean;
	// The event already carries a session — generation skips it rather than
	// overwriting a coach's own work.
	hasSession: boolean;
	// The booked resource behind an allocation practice; null for practices
	// created straight in the calendar.
	resource: PracticeResource | null;
}

export interface PracticeResource {
	title: string;
	parentTitle: string;
	locationTitle: string;
}

export interface BulkAddEventsResult {
	addedEventIds: number[];
	// Already in the practice set.
	skippedEventIds: number[];
}

export interface BulkRemoveEventsResult {
	removedEventIds: number[];
	// Were not in the practice set.
	skippedEventIds: number[];
}

export interface RecurringPracticesPayload {
	title: string;
	location?: string;
	// Weekdays the practice recurs on, 0 = Monday … 6 = Sunday.
	weekdays: number[];
	// Local wall-clock start, "HH:MM", read in timezone.
	startTime: string;
	durationMinutes: number;
	// Unix seconds bounding the series, read as whole days in timezone; both
	// default to the plan's period.
	from?: number;
	to?: number;
	// TZ database name, e.g. Europe/Stockholm.
	timezone: string;
}

export interface RecurringPracticesResult {
	eventSeries: EventSeries;
	// How many practices the series expands to. The events are written by a
	// background job, so they reach the practice set a moment after this
	// response — poll listPracticeSet until this many events of the series
	// are in it.
	expectedCount: number;
}

export interface StructureSegmentFocusPayload {
	libraryTagPublicId: string;
	weight: number;
}

export interface StructureSegmentPayload {
	// Absent for a segment added in this save.
	id?: number;
	title: string;
	description: string;
	order: number;
	duration: number;
	mode: models.sessionBlock.SessionBlockMode;
	generateContent: boolean;
	// The segment's full focus; omitted = keep what is stored.
	focus?: StructureSegmentFocusPayload[];
	items: [];
}

export interface PracticePlanGroupFocusPayload {
	libraryTagPublicId: string;
	weight: number;
}

export interface PracticePlanGroupUpdatePayload {
	generateSessions?: boolean;
	// Refused by the API when 360Player content is not available to the
	// organization or the team's sport — check ContentPool.curatedAvailable
	// before offering it — or when switching it off would leave no source.
	useCuratedContent?: boolean;
	// Refused by the API when switching it off would leave no source on.
	useOwnContent?: boolean;
	// A change rebuilds the structure template with or without the warm-up.
	includeWarmup?: boolean;
	// 1–10; a change rebuilds the structure template as that many blocks.
	blockCount?: number;
	// 1–10.
	exercisesPerBlock?: number;
	// The whole focus mix: omitted keeps what is stored, an empty list clears
	// it.
	focus?: PracticePlanGroupFocusPayload[];
	status?: PracticePlanGroupStatus;
}

export interface ContentPoolSource {
	exercises: number;
	sessions: number;
}

export interface ContentPool {
	// The team's library, counted whether or not it is in use.
	own: ContentPoolSource;
	// The plan group's toggle for the team's library.
	useOwnContent: boolean;
	// Whether 360Player content can be offered at all (curated library on the
	// organization, curated content for the team's sport).
	curatedAvailable: boolean;
	// What 360Player content would add; zero when unavailable.
	curated: ContentPoolSource;
	// The plan group's toggle, which total and exercisesByTag were computed
	// over.
	useCuratedContent: boolean;
	// The pool as it stands: the sources in use, added together.
	total: ContentPoolSource;
	// Exercises per library tag public id, with the focus mix's rollup: an
	// exercise tagged with a child counts for the child and its parent. Tags
	// with no exercises have no entry.
	exercisesByTag: { [publicId: string]: number };
	// The visible library tags the engine reads as warm-up content. Their
	// exercises fill the warm-up block alone and stay out of every other
	// block, so a focus weight on one could never be served: the focus
	// pickers leave them out.
	warmupTagPublicIds: string[];
}

export enum GenerationRunScope {
	Plan = 'plan',
	Week = 'week',
	Practice = 'practice',
	Block = 'block',
	Item = 'item',
}

export enum GenerationRunStatus {
	// Created and queued, the job has not picked it up yet.
	Pending = 'pending',
	// The job is writing sessions.
	Running = 'running',
	// Every practice was handled; the counts say how it went.
	Completed = 'completed',
	// Stopped on an error, recorded in errorMessage. Sessions written before
	// the failure stay.
	Failed = 'failed',
}

export interface GenerationRunTarget {
	eventId: number;
	trainingSessionId: number;
	trainingSessionBlockId: number;
	trainingSessionBlockItemId?: number;
	sessionGroupId?: number;
}

export interface GenerationRun extends Record, Dateable {
	practicePlanGroupId: number;
	requestedByUserId?: number;
	scope: GenerationRunScope;
	// Narrows the scope: the week start for the week scope, the event for
	// the practice scope, the block or item for the block and item scopes;
	// absent for the plan scope.
	targetId?: number;
	status: GenerationRunStatus;
	// How many exercises the run could draw from.
	contentPoolSize: number;
	// Practices that got a session, and practices that already carried one
	// and were left alone. A block or item run counts the items it wrote
	// instead.
	generatedCount: number;
	skippedCount: number;
	errorMessage?: string;
	startedAt?: number;
	finishedAt?: number;
	// What a block or item run rewrites, so the session can be re-read once
	// the run completes. Only on the run a start returns.
	affected?: GenerationRunTarget;
}

export interface StartGenerationRunPayload {
	// How much the run covers; plan when omitted. Plan, week and practice
	// fill practices; block and item work inside one generated session.
	scope?: GenerationRunScope;
	// The week start as unix seconds for the week scope, the event id for the
	// practice scope, the block or item id for the block and item scopes.
	targetId?: number;
	// With the block scope on a split block: rewrite only this session
	// group's items, the other groups' stay.
	sessionGroupId?: number;
	// Regenerate: the generated sessions in scope that players have not seen
	// are removed first and written anew. Released sessions and the coach's
	// own always stay; edited generated sessions stay unless overwriteEdited.
	// The block and item scopes always rewrite what they name.
	replace?: boolean;
	// Lets replace into sessions the coach edited by hand, and lets a block
	// or item run into one at all — without it such a start is refused.
	overwriteEdited?: boolean;
	// Lets replace into sessions the players can already open — the rewrite
	// comes back hidden, to be released again — and lets a block or item run
	// into one at all; without it such a start is refused.
	overwriteReleased?: boolean;
}

export enum ReviewWarningKind {
	// The same exercise sits in two items of the practice.
	RepeatInPractice = 'repeat_in_practice',
	// The exercise was also in the practice right before this one.
	RepeatFromPrevious = 'repeat_from_previous',
	// A block the engine was meant to fill got nothing.
	EmptyBlock = 'empty_block',
}

export interface ReviewWarning {
	kind: ReviewWarningKind;
	exerciseId?: number;
	exerciseTitle?: string;
	blockId?: number;
	blockTitle?: string;
	// The practice the exercise was also used in.
	otherEventId?: number;
}

export interface ReviewPractice {
	event: Event;
	// Null for a practice without one.
	session: models.session.Session | null;
	// A run wrote the session.
	generated: boolean;
	// Players can open the session.
	released: boolean;
	// The coach saved the session after the run that wrote it finished.
	edited: boolean;
	warnings: ReviewWarning[];
}

export interface ReviewMixTag {
	libraryTag: LibraryTag;
	intended: number;
	realized: number;
	// How many of the slot's items carry the tag.
	items: number;
}

export interface ReviewMix {
	sessionGroup?: models.sessionGroup.SessionGroup;
	blockTitle?: string;
	// Generated items in the slot across the plan.
	items: number;
	tags: ReviewMixTag[];
}

export interface ReviewSummary {
	practices: number;
	generated: number;
	released: number;
	unreleased: number;
	edited: number;
	ownSessions: number;
	withoutSession: number;
	warnings: number;
}

export interface Review {
	// Oldest first.
	practices: ReviewPractice[];
	mix: ReviewMix[];
	summary: ReviewSummary;
	// Exercises in the sessions that came from 360Player's content rather
	// than the team's own library.
	curatedExerciseIds: number[];
}

export interface ReleasePayload {
	// Release only these practices; omitted releases every practice in the
	// set. Every one must be in the practice set (400 otherwise).
	eventIds?: number[];
}

export interface ReleaseResult {
	// Generated sessions that became visible to players.
	releasedCount: number;
	// Generated sessions of the practice set still hidden afterwards.
	remainingCount: number;
	// The plan group with the status the release moved it to.
	planGroup: PracticePlanGroup;
}

export interface EventPracticePlanRef {
	practicePlanId: number;
	practicePlanGroupId: number;
	title: string;
	source: PracticePlanGroupEventSource;
}

export interface PracticeReview {
	practice: ReviewPractice;
	mix: ReviewMix[];
	curatedExerciseIds: number[];
}

export interface EventPracticePlanMembership {
	plan: PracticePlan;
	planGroup: PracticePlanGroup;
	source: PracticePlanGroupEventSource;
	review: PracticeReview;
}

export interface EventPracticePlan {
	memberships: EventPracticePlanMembership[];
}
